import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { refOn, siteId } from '@omnea-app/packages/universal/references/refKinds'
import { OverrideIconTypeSchema, VisibleIfSchema } from './BaseFormQuestionAndAnswerSchemas'
import { QuestionComputationSchema } from './QuestionComputation'

extendZodWithOpenApi(z)

/**
 * Per-instance fields shared by every variant — what the Form Template's per-question entry
 * in FormConfig carries regardless of question type.
 *
 * TODO (Question Syncing): this should grow as we identify more fields that every question type
 * needs to define at the instance level.
 */
export const BaseQuestionInstanceSchema = z.object({
  id: siteId(z.string(), 'question'),
  visibleIf: VisibleIfSchema,
  title: z.string().optional(),
  description: z.string().optional(),
  isRequired: z.boolean().optional(),
  readOnly: z.boolean().optional(),
  computation: QuestionComputationSchema.optional(),
})
export type BaseQuestionInstance = z.infer<typeof BaseQuestionInstanceSchema>

/**
 * Fields **intrinsic** to a Question: properties of the Question itself that should
 * be consistent across all instances. Persisted on `question_versions.config`.
 *
 * TODO (Question Syncing): this should shrink as we identify which fields are truly intrinsic
 * and should be the same across all instances.
 */
export const BaseQuestionDefinitionSchema = z.object({
  name: z.string(),
  disabled: z.boolean().optional(),
  weighting: z.number().optional(),
  tooltip: z.string().optional(),
  doubleEntryEnabled: z.boolean().optional(),
  overrideIconType: OverrideIconTypeSchema.optional(),
  isScorable: z.boolean().optional(),
  autoFlaggedScoreCategoryIds: z.array(refOn(z.uuid(), 'autoFlaggedScoreCategory')).optional(),
  isMaterial: z.boolean().optional(),
})
export type BaseQuestionDefinition = z.infer<typeof BaseQuestionDefinitionSchema>

/**
 * Fields that are **overrideable** per Form Template instance. Persisted on
 * `question_versions.config` as a default (required here), and mirrored on
 * `BaseQuestionInstanceSchema` as optional override slots.
 *
 * TODO (Question Syncing): this should grow as we identify more fields that are instance related,
 * but can have an overrideable default value.
 */
export const BaseQuestionConfigSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  isRequired: z.boolean(),
  readOnly: z.boolean().optional(),
})

export type BaseQuestionConfig = z.infer<typeof BaseQuestionConfigSchema>

/**
 * Overrideable-default fields made optional on the *stored* Question config — a Question may omit a default and
 * let each Form instance supply it (the resolved `FormQuestion` still requires them; see `QuestionConfigSchema`).
 */
export const OVERRIDEABLE_DEFAULT_FIELD_MASK = {
  title: true,
  description: true,
  isRequired: true,
  readOnly: true,
} as const satisfies Record<keyof BaseQuestionConfig, true>

/**
 * Stored link from a Form Template's per-question entry to an Organisation Question. Carries the
 * Question `id` only — the Version the Form Template tracks (and whether it is pinned) lives in the
 * `form_templates_question_versions` link table, never in the `form_templates.config` column.
 */
export const OrganisationQuestionLinkSchema = z.object({
  id: refOn(z.uuid(), 'organisationQuestion'),
})
export type OrganisationQuestionLink = z.infer<typeof OrganisationQuestionLinkSchema>

/**
 * An Organisation Question link plus the tracked Version and pin state — the shape stored in the
 * `form_templates_question_versions` link table. No resolved-config metadata; see {@link ResolvedOrganisationQuestionLinkSchema}.
 */
export const OrganisationQuestionVersionLinkSchema = z.object({
  ...OrganisationQuestionLinkSchema.shape,
  versionId: z.number().int().positive(),
  isQuestionVersionPinned: z.boolean(),
})
export type OrganisationQuestionVersionLink = z.infer<typeof OrganisationQuestionVersionLinkSchema>

// Bounded union of the value shapes a default can take (`null` = no default). Keep it concrete: `z.unknown()`
// breaks the AI SDK `JSONValue` the resolved config streams through, and `z.json()` breaks Immer's
// `Draft<FormConfig>`. Extend only when a new value *shape* becomes overrideable.
const QuestionFieldDefaultSchema = z.union([z.string(), z.boolean(), z.null()])

/** Fields a Form may override, keyed by field name → default. Absent keys are Question-owned (not editable). */
export const OverrideableFieldsSchema = z.record(z.string(), QuestionFieldDefaultSchema)
export type OverrideableFields = z.infer<typeof OverrideableFieldsSchema>

/**
 * A resolved Organisation Question link: the tracked Version/pin link plus the resolver-stamped
 * `overrideableFields`. `overrideableFields` is resolver-owned read metadata — the backend resolver stamps it on
 * every read, but it's optional here so write/in-memory links (which never carry it) stay valid.
 */
export const ResolvedOrganisationQuestionLinkSchema = z.object({
  ...OrganisationQuestionVersionLinkSchema.shape,
  overrideableFields: OverrideableFieldsSchema.optional(),
})
export type ResolvedOrganisationQuestionLink = z.infer<typeof ResolvedOrganisationQuestionLinkSchema>

/**
 * Storage shape of a Linked entry on a Form Template's per-question array — carries the
 * per-instance fields plus the link payload. No `type`, no variant-specific fields; those are
 * resolved at render time from the linked Question's Definition + Config.
 *
 * The entry is sparse: it stores only the fields a Form actually sets, so a question carrying no
 * visibility condition omits `visibleIf` altogether. The resolved `FormQuestion` still requires it.
 */
export const LinkedFormQuestionSchema = BaseQuestionInstanceSchema.partial({ visibleIf: true })
  .extend({
    organisationQuestion: OrganisationQuestionLinkSchema,
  })
  .strict()
export type LinkedFormQuestion = z.infer<typeof LinkedFormQuestionSchema>
